// Names the screen gives to chats, durations and costs.
import {
  type AgentDeviceDTO,
  THIS_PC_NAME,
} from "@trameria/core/agent-chat-shared";
import { m } from "@trameria/i18n";
import { AGENT_KIND_LABEL } from "@trameria/shared-agent";
import { formatNumber } from "../time/format";

export const chatTitle = (chat: { title: string | null }): string =>
  chat.title?.trim() || m.agents_untitled();

/**
 * A device as the screen shows it: the offline edition's device, while it
 * keeps the name it was made with, is named in the screen's language.
 */
export function shownDevice(d: AgentDeviceDTO): AgentDeviceDTO {
  return d.kind === "this_pc" && d.name === THIS_PC_NAME
    ? { ...d, name: m.agents_this_pc_name() }
    : d;
}

/**
 * The product name of an agent kind ("Claude Code", "Codex"), else what the
 * device calls it, else the kind as stored. A device that is gone or has not
 * said what it has still shows "Codex", not "codex".
 */
export function agentName(
  kind: string | null,
  deviceLabel?: string | null,
): string {
  const known = kind
    ? (AGENT_KIND_LABEL as Partial<Record<string, string>>)[kind]
    : undefined;
  return known ?? deviceLabel ?? kind ?? "";
}

/** "12 s" / "3 min 4 s" / "1 h 2 min", in the screen's language. */
export function durationText(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return m.agents_seconds({ n: s });
  const min = Math.floor(s / 60);
  if (min < 60) return m.agents_minutes_seconds({ m: min, s: s % 60 });
  return m.agents_hours_minutes({ h: Math.floor(min / 60), m: min % 60 });
}

export const costText = (usd: number): string =>
  `$${usd < 10 ? usd.toFixed(2) : formatNumber(Math.round(usd))}`;

/** "139k" / "1.2M" for token counts. */
export function tokensText(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${Math.round(n / 100_000) / 10}M`;
}
